import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getBankSettings, updateBankSettings } from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { confirm } from '../../shared/ui/confirmStore'
import { toast } from '../../shared/ui/toastStore'
import {
  BANK_OPEN_LINES_KEY,
  BANK_SETTINGS_KEY,
  BANK_STATEMENTS_KEY,
  formatBankDate,
} from './bankFormat'

/**
 * Дата начала учёта (14.10.2026): прошлый период сверяется в 1С, в микрохабе его
 * не разносим. Оплаты по счетам до этой даты программа сама помечает «не учитываем»
 * — при загрузке выписки и сразу для уже загруженных неразнесённых строк.
 */
export function BankStartDateCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const settings = useQuery({ queryKey: [...BANK_SETTINGS_KEY], queryFn: getBankSettings })
  const saved = settings.data?.accountingStartDate ?? null
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => setValue(saved ?? ''), [saved])

  if (!settings.data) return null

  const save = async (next: string | null) => {
    const ok = await confirm({
      title: next ? `Начать учёт с ${formatBankDate(next)}?` : 'Снять дату начала учёта?',
      message: next
        ? 'Неразнесённые оплаты по счетам до этой даты уйдут в «Не учитываем». Строки с разнесёнными оплатами не меняются.'
        : 'Оплаты, убранные по дате начала учёта, вернутся в очередь «Не разнесено».',
      confirmText: 'Сохранить',
    })
    if (!ok) return
    setSaving(true)
    try {
      const r = await updateBankSettings(next)
      qc.setQueryData([...BANK_SETTINGS_KEY], r.settings)
      void qc.invalidateQueries({ queryKey: [...BANK_STATEMENTS_KEY] })
      void qc.invalidateQueries({ queryKey: [...BANK_OPEN_LINES_KEY] })
      void qc.invalidateQueries({ queryKey: ['bank-statement'] })
      const parts = [
        r.skipped > 0 ? `убрано в «Не учитываем» — ${r.skipped}` : '',
        r.reopened > 0 ? `возвращено в работу — ${r.reopened}` : '',
      ].filter(Boolean)
      toast.success(
        'Дата начала учёта сохранена' + (parts.length > 0 ? `: ${parts.join(', ')}` : ''),
      )
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось сохранить дату начала учёта'))
    } finally {
      setSaving(false)
    }
  }

  const changed = (value || null) !== saved

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-sm font-medium text-gray-900">Начало учёта</h2>
      <p className="mt-1 text-xs text-gray-500">
        Оплаты по счетам, выставленным раньше этой даты, программа не разносит и помечает «Не
        учитываем» — прошлый период сверяется в 1С.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        {canEdit ? (
          <>
            <input
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="Дата начала учёта"
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
            />
            <button
              type="button"
              disabled={!changed || !value || saving}
              onClick={() => void save(value)}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              Сохранить
            </button>
            {saved && (
              <button
                type="button"
                disabled={saving}
                onClick={() => void save(null)}
                className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Снять дату
              </button>
            )}
          </>
        ) : (
          <span className="text-gray-900">{saved ? formatBankDate(saved) : 'Не задана'}</span>
        )}
        {!saved && canEdit && (
          <span className="text-xs text-orange-600">Не задана — разносятся все поступления</span>
        )}
      </div>
    </div>
  )
}
